// services/gql.js
import httpClient from "@/services/httpClient";
import { GRAPHQL_PATH } from "@/services/config";

/**
 * Universal GraphQL request.
 *
 * Behavior:
 * - Sends a POST via the shared httpClient (Axios).
 * - Unified error policy:
 *   1) Network/HTTP failure (no 2xx): throw Error(message) and attach:
 *      - .status (HTTP status or null)
 *      - .isNetworkError (true if no HTTP response at all)
 *   2) 2xx but GraphQL-level errors (data.errors[]): throw Error(first message)
 *      and attach .graphQLErrors = data.errors
 *   3) 2xx but malformed payload (no data.data): throw Error("Malformed GraphQL response...")
 *
 * Returns:
 * - Only the GraphQL `data` object (i.e., payload.data).
 *
 * @param {Object} params
 * @param {string} params.query        - GraphQL document string
 * @param {Object} [params.variables]  - GraphQL variables (default: {})
 * @param {string} [params.path]       - Endpoint path, defaults to GRAPHQL_PATH
 * @returns {Promise<any>}             - payload.data (the GraphQL data object)
 */
export async function request({ query, variables = {}, path = GRAPHQL_PATH }) {
    let resp;

    try {
        resp = await httpClient.post(path, { query, variables });
    } catch (err) {
        // Axios error:
        // - err.response present => HTTP error (non-2xx)
        // - err.response absent  => network error (CORS/offline/timeout)
        const status = err?.response?.status ?? null;
        const gqlMsg = err?.response?.data?.errors?.[0]?.message;
        const serverMsg = err?.response?.data?.message || err?.response?.statusText;

        const e = new Error(gqlMsg || serverMsg || err?.message || "Network error");
        e.status = status;
        e.isNetworkError = !err?.response;
        throw e;
    }

    const { data } = resp;

    // Empty/undefined payload
    if (!data) {
        throw new Error("Empty GraphQL response");
    }

    // GraphQL-level errors inside a 2xx HTTP response
    if (Array.isArray(data.errors) && data.errors.length) {
        const e = new Error(data.errors[0]?.message || "GraphQL error");
        e.graphQLErrors = data.errors;
        throw e;
    }

    // Well-formed GraphQL response must have `data`
    if (typeof data.data === "undefined") {
        throw new Error("Malformed GraphQL response: missing 'data'");
    }

    return data.data;
}

export default { request };
